import { useQuery } from '@tanstack/react-query';
import { fetchWithBypass } from '@/lib/platform/utils/fetch';

export interface ExchangeRatesResponse {
  rates: Record<string, number>;
  currencyMetadata?: Record<string, { name: string; symbol: string; format: string }>; // From currencies table
  source: 'live' | 'cache' | 'cache-expired' | 'database' | 'database-expired' | 'error';
  lastUpdated: string;
  nextUpdate?: string;
  cacheExpiresIn: number; // seconds
  totalCurrencies?: number; // Total number of currencies in response
  error?: string;
}

/**
 * useExchangeRates Hook
 *
 * Fetches live exchange rates from ExchangeRate-API.com via `/api/exchange-rates`
 * Rates are cached for 24 hours server-side
 * React Query provides additional client-side caching (5 minutes stale time)
 *
 * @returns Exchange rates relative to DKK, source, and last updated timestamp
 *
 * @example
 * const { data, isLoading, error } = useExchangeRates();
 * console.log(data?.rates.EUR); // 0.1340
 * console.log(data?.source); // 'live' | 'cache' | 'database' | 'error'
 * console.log(data?.lastUpdated); // '2026-01-31T12:00:00Z'
 */
export function useExchangeRates() {
  return useQuery<ExchangeRatesResponse>({
    queryKey: ['exchange-rates'],
    queryFn: async () => {
      const response = await fetchWithBypass('/api/exchange-rates');

      if (!response.ok) {
        // Let React Query handle errors - no client-side fallback
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch exchange rates: ${response.status}`);
      }

      const data = await response.json();
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes (client-side cache)
    gcTime: 60 * 60 * 1000, // 1 hour (garbage collection)
    retry: 1, // Only retry once on failure
    refetchOnWindowFocus: false, // Don't refetch on window focus (rates don't change that often)
  });
}
